import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  QrCode as QrCodeIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  TrendingUp as StatsIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import toastUtils from '../../../../utils/toastUtils';
import api from '../../../../services/api';
import { i18n } from '../../../../translate/i18n';
import AffiliateStatsDialog from '../dialogs/AffiliateStatsDialog';

const AffiliatesTab = ({ landingPage }) => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [qrDialog, setQrDialog] = useState({ open: false, affiliate: null });
  const [statsDialog, setStatsDialog] = useState({ open: false, affiliate: null });
  const [menuAnchor, setMenuAnchor] = useState({ el: null, affiliate: null });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    commissionRate: 10,
    active: true
  });

  // Carregar afiliados da landing page
  useEffect(() => {
    if (landingPage?.id) {
      loadAffiliates();
    }
  }, [landingPage?.id]);

  const loadAffiliates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/affiliates`, {
        params: {
          landingPageId: landingPage.id
        }
      });
      setAffiliates(response.data.affiliates || []);
    } catch (error) {
      console.error('Erro ao carregar afiliados:', error);
      toastUtils.error("Erro no Afiliado", i18n.t("landingPages.affiliates.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAffiliate = async () => {
    try {
      const response = await api.post('/affiliates', {
        ...formData,
        landingPageId: landingPage.id
      });
      
      setAffiliates(prev => [...prev, response.data]);
      handleCloseDialog();
      toastUtils.success("Afiliado Criado", i18n.t("landingPages.affiliates.createSuccess"));
    } catch (error) {
      console.error('Erro ao criar afiliado:', error);
      toastUtils.error("Erro no Afiliado", i18n.t("landingPages.affiliates.createError"));
    }
  };

  const handleUpdateAffiliate = async () => {
    try {
      const response = await api.put(`/affiliates/${editingAffiliate.id}`, formData);
      
      setAffiliates(prev => prev.map(affiliate => 
        affiliate.id === editingAffiliate.id ? response.data : affiliate
      ));
      handleCloseDialog();
      toastUtils.success("Afiliado Atualizado", i18n.t("landingPages.affiliates.updateSuccess"));
    } catch (error) {
      console.error('Erro ao atualizar afiliado:', error);
      toastUtils.error("Erro no Afiliado", i18n.t("landingPages.affiliates.updateError"));
    }
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    if (!window.confirm(i18n.t("landingPages.affiliates.confirmDelete"))) {
      return;
    }

    try {
      await api.delete(`/affiliates/${affiliateId}`);
      setAffiliates(prev => prev.filter(affiliate => affiliate.id !== affiliateId));
      toastUtils.success("Afiliado Excluído", i18n.t("landingPages.affiliates.deleteSuccess"));
    } catch (error) {
      console.error('Erro ao excluir afiliado:', error);
      toastUtils.error("Erro no Afiliado", i18n.t("landingPages.affiliates.deleteError"));
    }
  };

  const handleOpenDialog = (affiliate = null) => {
    if (affiliate) {
      setEditingAffiliate(affiliate);
      setFormData({
        name: affiliate.name,
        email: affiliate.email || '',
        phone: affiliate.phone || '',
        commissionRate: affiliate.commissionRate,
        active: affiliate.active
      });
    } else {
      setEditingAffiliate(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        commissionRate: 10,
        active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAffiliate(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      commissionRate: 10,
      active: true
    });
  };

  const handleCopyLink = (affiliate) => {
    const baseUrl = window.location.origin;
    const affiliateLink = `${baseUrl}/lp/${landingPage.slug}?affiliate=${affiliate.code}`;
    
    navigator.clipboard.writeText(affiliateLink).then(() => {
      toastUtils.success("Link Copiado", i18n.t("landingPages.affiliates.linkCopied"));
    }).catch(() => {
      toastUtils.error("Erro no Link", i18n.t("landingPages.affiliates.linkCopyError"));
    });
  };

  const handleShowQR = (affiliate) => {
    setQrDialog({ open: true, affiliate });
  };

  const handleShowStats = (affiliate) => {
    setStatsDialog({ open: true, affiliate });
  };

  const handleMenuOpen = (event, affiliate) => {
    setMenuAnchor({ el: event.currentTarget, affiliate });
  };

  const handleMenuClose = () => {
    setMenuAnchor({ el: null, affiliate: null });
  };

  const getAffiliateUrl = (affiliate) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/lp/${landingPage.slug}?affiliate=${affiliate.code}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          {i18n.t("landingPages.affiliates.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {i18n.t("landingPages.affiliates.newAffiliate")}
        </Button>
      </Box>

      {affiliates.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {i18n.t("landingPages.affiliates.noAffiliatesTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              {i18n.t("landingPages.affiliates.noAffiliatesMessage")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {i18n.t("landingPages.affiliates.createFirst")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {affiliates.map((affiliate) => (
            <Grid item xs={12} md={6} lg={4} key={affiliate.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        {affiliate.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" noWrap>
                          {affiliate.name}
                        </Typography>
                        <Chip
                          label={affiliate.active ? i18n.t("landingPages.affiliates.active") : i18n.t("landingPages.affiliates.inactive")}
                          color={affiliate.active ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, affiliate)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {i18n.t("landingPages.affiliates.code")}: <strong>{affiliate.code}</strong>
                    </Typography>
                    {affiliate.email && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        📧 {affiliate.email}
                      </Typography>
                    )}
                    {affiliate.phone && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        📱 {affiliate.phone}
                      </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {i18n.t("landingPages.affiliates.commissionRate")}: <strong>{affiliate.commissionRate}%</strong>
                    </Typography>
                  </Box>

                  {/* Estatísticas básicas */}
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                    <Grid container spacing={2} textAlign="center">
                      <Grid item xs={4}>
                        <Typography variant="h6" color="primary">
                          {affiliate.metadata?.tracking?.clicks || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {i18n.t("landingPages.affiliates.clicks")}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="h6" color="success.main">
                          {affiliate.metadata?.tracking?.conversions || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {i18n.t("landingPages.affiliates.conversions")}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="h6" color="info.main">
                          {affiliate.metadata?.tracking?.clicks > 0 
                            ? ((affiliate.metadata.tracking.conversions || 0) / affiliate.metadata.tracking.clicks * 100).toFixed(1)
                            : 0}%
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {i18n.t("landingPages.affiliates.rate")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Ações rápidas */}
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => handleCopyLink(affiliate)}
                      fullWidth
                    >
                      {i18n.t("landingPages.affiliates.copyLink")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<QrCodeIcon />}
                      onClick={() => handleShowQR(affiliate)}
                    >
                      {i18n.t("landingPages.affiliates.qr")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<StatsIcon />}
                      onClick={() => handleShowStats(affiliate)}
                    >
                      {i18n.t("landingPages.affiliates.stats")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Menu de ações */}
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(menuAnchor.affiliate);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} /> {i18n.t("landingPages.affiliates.edit")}
        </MenuItem>
        <MenuItem onClick={() => {
          handleCopyLink(menuAnchor.affiliate);
          handleMenuClose();
        }}>
          <CopyIcon sx={{ mr: 1 }} /> {i18n.t("landingPages.affiliates.copyLink")}
        </MenuItem>
        <MenuItem onClick={() => {
          handleShowQR(menuAnchor.affiliate);
          handleMenuClose();
        }}>
          <QrCodeIcon sx={{ mr: 1 }} /> {i18n.t("landingPages.affiliates.generateQR")}
        </MenuItem>
        <MenuItem onClick={() => {
          handleShowStats(menuAnchor.affiliate);
          handleMenuClose();
        }}>
          <StatsIcon sx={{ mr: 1 }} /> {i18n.t("landingPages.affiliates.viewStats")}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleDeleteAffiliate(menuAnchor.affiliate.id);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> {i18n.t("landingPages.affiliates.delete")}
        </MenuItem>
      </Menu>

      {/* Dialog para criar/editar afiliado */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {editingAffiliate ? i18n.t("landingPages.affiliates.modalEdit") : i18n.t("landingPages.affiliates.modalCreate")}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18n.t("landingPages.affiliates.name")}
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={i18n.t("landingPages.affiliates.email")}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={i18n.t("landingPages.affiliates.phone")}
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18n.t("landingPages.affiliates.commissionRate")}
                type="number"
                value={formData.commissionRate}
                onChange={(e) => setFormData(prev => ({ ...prev, commissionRate: parseFloat(e.target.value) }))}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  />
                }
                label={i18n.t("landingPages.affiliates.activeLabel")}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button onClick={handleCloseDialog}>
              {i18n.t("landingPages.affiliates.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={editingAffiliate ? handleUpdateAffiliate : handleCreateAffiliate}
            >
              {editingAffiliate ? i18n.t("landingPages.affiliates.update") : i18n.t("landingPages.affiliates.create")}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog do QR Code */}
      <Dialog
        open={qrDialog.open}
        onClose={() => setQrDialog({ open: false, affiliate: null })}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {i18n.t("landingPages.affiliates.qrTitle", { name: qrDialog.affiliate?.name })}
          </Typography>
          
          {qrDialog.affiliate && (
            <Box sx={{ my: 3 }}>
              <QRCodeSVG
                value={getAffiliateUrl(qrDialog.affiliate)}
                size={200}
                level="H"
                includeMargin
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2, wordBreak: 'break-all' }}>
                {getAffiliateUrl(qrDialog.affiliate)}
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <Button onClick={() => setQrDialog({ open: false, affiliate: null })}>
              {i18n.t("landingPages.affiliates.close")}
            </Button>
            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={() => {
                handleCopyLink(qrDialog.affiliate);
                setQrDialog({ open: false, affiliate: null });
              }}
            >
              {i18n.t("landingPages.affiliates.copyLink")}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog de estatísticas */}
      <AffiliateStatsDialog
        open={statsDialog.open}
        onClose={() => setStatsDialog({ open: false, affiliate: null })}
        affiliate={statsDialog.affiliate}
      />
    </Box>
  );
};

export default AffiliatesTab;